import { useState, useMemo } from 'react';
import { BookOpen, Plus, X, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { format } from 'date-fns';
import { useEmployees } from '@/api/queries/employee.queries';
import { useLeaveLedger, useLeaveAdjustment, useLeaveEncashment, useLeaveBalances } from '@/api/queries/leave.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { toast } from 'sonner';

// ─── Transaction type metadata ─────────────────────────────────────────────────

const TXN_META = {
  OPENING_BALANCE: { label: 'Opening Balance', color: 'bg-gray-100 text-gray-700',   sign: '+' },
  ACCRUAL:         { label: 'Monthly Accrual',  color: 'bg-green-100 text-green-700', sign: '+' },
  APPLICATION:     { label: 'Leave Applied',    color: 'bg-amber-100 text-amber-700', sign: '-' },
  APPROVAL:        { label: 'Approved',          color: 'bg-green-50 text-green-600',  sign: ' ' },
  REJECTION:       { label: 'Rejected (restored)', color: 'bg-blue-100 text-blue-700', sign: '+' },
  CANCELLATION:    { label: 'Cancelled (restored)',color: 'bg-blue-100 text-blue-700', sign: '+' },
  'ADJUSTMENT+':   { label: 'Manual Credit',    color: 'bg-purple-100 text-purple-700', sign: '+' },
  'ADJUSTMENT-':   { label: 'Manual Debit',     color: 'bg-red-100 text-red-700',     sign: '-' },
  ENCASHMENT:      { label: 'Encashed',          color: 'bg-orange-100 text-orange-700', sign: '-' },
  CARRY_FORWARD:   { label: 'Carry Forward',     color: 'bg-teal-100 text-teal-700',   sign: '+' },
  LAPSE:           { label: 'Lapsed',            color: 'bg-red-100 text-red-700',     sign: '-' },
  LWP:             { label: 'Loss of Pay',       color: 'bg-red-100 text-red-700',     sign: '-' },
};

const LEAVE_TYPES = [
  { code: 'EL', name: 'Earned Leave', color: '#22c55e' },
  { code: 'CL', name: 'Casual Leave', color: '#3b82f6' },
  { code: 'SL', name: 'Sick Leave',   color: '#f59e0b' },
  { code: 'CO', name: 'Comp-Off',     color: '#0ea5e9' },
];

// ─── Adjustment Modal ─────────────────────────────────────────────────────────

function AdjustmentModal({ employee, onClose }) {
  const adjust = useLeaveAdjustment();
  const [form, setForm] = useState({
    leave_code: 'EL',
    direction: 'CREDIT',
    days: '',
    effective_date: new Date().toISOString().slice(0, 10),
    reason: '',
  });

  const handleSubmit = () => {
    if (!form.days || Number(form.days) <= 0) { toast.error('Enter days'); return; }
    if (!form.reason.trim()) { toast.error('Enter reason'); return; }
    adjust.mutate({ employee_id: employee.id, ...form, days: Number(form.days) }, {
      onSuccess: () => { toast.success(`${form.direction === 'CREDIT' ? '+' : '-'}${form.days} ${form.leave_code} adjusted`); onClose(); },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-md border border-[var(--border)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Manual Adjustment</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-[var(--text-secondary)]">Adjusting balance for <strong>{`${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()}</strong></p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Leave Type</label>
              <select value={form.leave_code} onChange={e => setForm(f => ({ ...f, leave_code: e.target.value }))} className="input-field">
                {LEAVE_TYPES.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Direction</label>
              <select value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))} className="input-field">
                <option value="CREDIT">Credit (+)</option>
                <option value="DEBIT">Debit (−)</option>
              </select>
            </div>
            <div>
              <label className="form-label">Days</label>
              <input type="number" step="0.5" min="0.5" value={form.days} onChange={e => setForm(f => ({ ...f, days: e.target.value }))} placeholder="e.g. 2" className="input-field" />
            </div>
            <div>
              <label className="form-label">Effective Date</label>
              <input type="date" value={form.effective_date} onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} className="input-field" />
            </div>
          </div>
          <div>
            <label className="form-label">Reason *</label>
            <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={2}
              placeholder="Reason for adjustment (visible in ledger)"
              className="input-field resize-none" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)]">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={adjust.isPending}
            className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
            {adjust.isPending ? 'Saving…' : 'Apply Adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Encashment Modal ─────────────────────────────────────────────────────────

function EncashmentModal({ employee, basicSalary, onClose }) {
  const encash = useLeaveEncashment();
  const [days, setDays] = useState('');
  const perDay = basicSalary > 0 ? Math.round(basicSalary / 26) : 0;
  const amount = days && perDay ? Number(days) * perDay : 0;

  const handleSubmit = () => {
    if (!days || Number(days) <= 0) { toast.error('Enter days to encash'); return; }
    encash.mutate({ employee_id: employee.id, days: Number(days), amount_per_day: perDay, total_amount: amount }, {
      onSuccess: () => { toast.success(`EL encashment of ₹${amount.toLocaleString('en-IN')} processed`); onClose(); },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-sm border border-[var(--border)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Process EL Encashment</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-[var(--text-secondary)]">
            EL encashment for <strong>{`${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()}</strong><br />
            Rate: <strong>Basic ÷ 26 = ₹{perDay.toLocaleString('en-IN')}/day</strong>
          </p>
          <div>
            <label className="form-label">Days to Encash</label>
            <input type="number" step="1" min="1" value={days} onChange={e => setDays(e.target.value)} placeholder="Enter days" className="input-field" />
          </div>
          {days && amount > 0 && (
            <div className="p-3 rounded-[10px] bg-[var(--accent-muted)] border border-[var(--accent)]/30">
              <p className="text-[13px] font-bold text-[var(--accent)]">Amount: ₹{amount.toLocaleString('en-IN')}</p>
              <p className="text-[11px] text-[var(--text-tertiary)]">{days} days × ₹{perDay.toLocaleString('en-IN')}/day (Basic/26)</p>
            </div>
          )}
          <p className="text-[11px] text-amber-600">Max encashable: 30 EL days per year. TDS applicable per slab.</p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)]">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={encash.isPending}
            className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
            {encash.isPending ? 'Processing…' : 'Confirm Encashment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Balance Summary Strip ────────────────────────────────────────────────────

// Normalize any leave balance item to a code-keyed entry
// API may return `type: 'EARNED_LEAVE'` or `leave_code: 'EL'` or `label: 'Earned Leave'`
const TYPE_CODE_MAP = {
  EARNED_LEAVE: 'EL', EL: 'EL', 'Earned Leave': 'EL',
  CASUAL_LEAVE: 'CL', CL: 'CL', 'Casual Leave': 'CL',
  SICK_LEAVE:   'SL', SL: 'SL', 'Sick Leave':   'SL',
  COMP_OFF:     'CO', CO: 'CO', 'Comp-Off':      'CO', 'Comp Off': 'CO',
};

function BalanceSummary({ balances }) {
  const items = [
    { code: 'EL', name: 'Earned Leave', color: '#22c55e' },
    { code: 'CL', name: 'Casual Leave', color: '#3b82f6' },
    { code: 'SL', name: 'Sick Leave',   color: '#f59e0b' },
    { code: 'CO', name: 'Comp-Off',     color: '#0ea5e9' },
  ];

  // Build lookup map normalizing leave_code / type / label field names
  const bMap = (balances || []).reduce((acc, b) => {
    const code = TYPE_CODE_MAP[b.leave_code] || TYPE_CODE_MAP[b.type] || TYPE_CODE_MAP[b.label];
    if (code) acc[code] = b;
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map(t => {
        const b = bMap[t.code];
        const avail = b?.available ?? b?.balance ?? 0;
        const used  = b?.used ?? 0;
        const total = b?.total ?? (avail + used);
        const pct   = total ? Math.round((avail / total) * 100) : 0;
        return (
          <div key={t.code} className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
              <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">{t.name}</p>
            </div>
            <div className="flex items-end gap-1">
              <p className="text-[22px] font-bold" style={{ color: t.color }}>{b ? avail : '—'}</p>
              {b && <p className="text-[11px] text-[var(--text-tertiary)] mb-1">/{total}</p>}
            </div>
            {b && (
              <>
                <div className="h-1.5 rounded-full bg-[var(--border)] mt-1">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: t.color }} />
                </div>
                <p className="text-[10px] text-[var(--text-tertiary)] mt-1">Used {used} · {pct}% remaining</p>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LeaveLedgerPage() {
  const { data: empData } = useEmployees({ limit: 200 });
  const employees = empData?.data || [];

  const [selectedEmp, setSelectedEmp] = useState(null);
  const [year, setYear]   = useState(new Date().getFullYear().toString());
  const [empSearch, setEmpSearch] = useState('');
  const [filterCode, setFilterCode] = useState('ALL');
  const [showAdjust, setShowAdjust] = useState(false);
  const [showEncash, setShowEncash] = useState(false);

  const { data: ledgerData } = useLeaveLedger(selectedEmp?.id, year);
  const { data: balancesData } = useLeaveBalances(selectedEmp?.id);

  const rawEntries = ledgerData?.data || [];
  const balances   = balancesData?.data || [];

  const filteredEmps = useMemo(() => {
    const q = empSearch.toLowerCase();
    return employees.filter(e => {
      const n = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
      return !q || n.includes(q) || (e.employee_code || '').toLowerCase().includes(q);
    });
  }, [employees, empSearch]);

  const entries = useMemo(() => {
    let e = [...rawEntries].sort((a, b) => a.date.localeCompare(b.date));
    if (filterCode !== 'ALL') e = e.filter(x => x.leave_code === filterCode);
    return e;
  }, [rawEntries, filterCode]);

  const basicSalary = selectedEmp?.salary?.basic || 0;

  return (
    <div className="flex flex-col gap-5 p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[20px] font-bold text-[var(--text-primary)]">Leave Ledger</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">Complete transaction history for any employee's leave balance</p>
        </div>
      </div>

      {/* Employee + Year selector */}
      <div className="card p-4 flex flex-wrap gap-4 items-start">
        <div className="flex-1 min-w-[240px]">
          <label className="form-label">Employee</label>
          {selectedEmp ? (
            <div className="flex items-center gap-2.5 p-2 rounded-[10px] bg-[var(--accent-muted)] border border-[var(--accent)]/30">
              <Avatar name={`${selectedEmp.first_name || ''} ${selectedEmp.last_name || ''}`.trim()} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[var(--text-primary)]">{`${selectedEmp.first_name || ''} ${selectedEmp.last_name || ''}`.trim()}</p>
                <p className="text-[10px] text-[var(--text-tertiary)]">{selectedEmp.employee_code}</p>
              </div>
              <button onClick={() => setSelectedEmp(null)} className="text-[var(--text-tertiary)] hover:text-[var(--danger)]"><X size={13} /></button>
            </div>
          ) : (
            <div className="border border-[var(--border)] rounded-[10px] overflow-hidden">
              <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employee…"
                className="w-full h-9 px-3 text-[12px] bg-[var(--bg-app)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none" />
              {empSearch && (
                <div className="max-h-36 overflow-y-auto border-t border-[var(--border)]">
                  {filteredEmps.slice(0, 8).map(emp => {
                    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                    return (
                      <button key={emp.id} onClick={() => { setSelectedEmp(emp); setEmpSearch(''); }}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-hover)] transition-colors text-left">
                        <Avatar name={name} size="xs" />
                        <div>
                          <p className="text-[12px] font-medium text-[var(--text-primary)]">{name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{emp.employee_code}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="form-label">Year</label>
          <select value={year} onChange={e => setYear(e.target.value)} className="input-field w-28">
            {['2024', '2025', '2026', '2027'].map(y => <option key={y}>{y}</option>)}
          </select>
        </div>

        {selectedEmp && (
          <div className="flex items-end gap-2">
            <button onClick={() => setShowAdjust(true)}
              className="flex items-center gap-1.5 h-9 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
              <Plus size={13} /> Manual Adjustment
            </button>
            <button onClick={() => setShowEncash(true)}
              className="flex items-center gap-1.5 h-9 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
              Process EL Encashment
            </button>
          </div>
        )}
      </div>

      {!selectedEmp ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <BookOpen size={32} className="text-[var(--text-tertiary)] opacity-40" />
          <p className="text-[14px] font-medium text-[var(--text-secondary)]">Select an employee to view their leave ledger</p>
        </div>
      ) : (
        <>
          {/* Balance summary */}
          <BalanceSummary balances={balances} />

          {/* Filter by leave type */}
          <div className="flex gap-1.5 flex-wrap">
            {['ALL', ...LEAVE_TYPES.map(t => t.code)].map(code => (
              <button key={code} onClick={() => setFilterCode(code)}
                className={cn('h-8 px-3 text-[12px] font-medium rounded-[8px] transition-all',
                  filterCode === code ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
                {code === 'ALL' ? 'All Types' : LEAVE_TYPES.find(t => t.code === code)?.name || code}
              </button>
            ))}
          </div>

          {/* Ledger table */}
          <div className="card p-0 overflow-hidden">
            {entries.length === 0 ? (
              <div className="text-center py-10 text-[13px] text-[var(--text-tertiary)]">No ledger entries for selected period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="border-b border-[var(--border)]">
                      {['Date', 'Leave Type', 'Transaction', 'Days', 'Balance Before', 'Balance After', 'By', 'Note'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => {
                      const meta  = TXN_META[e.txn_type] || TXN_META.OPENING_BALANCE;
                      const ltype = LEAVE_TYPES.find(t => t.code === e.leave_code);
                      return (
                        <tr key={e.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors">
                          <td className="px-4 py-2.5 whitespace-nowrap text-[var(--text-secondary)]">
                            {e.date ? format(new Date(e.date), 'dd MMM yyyy') : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: ltype?.color || '#9ca3af' }} />
                              <span className="font-medium text-[var(--text-primary)]">{ltype?.name || e.leave_code}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold', meta.color)}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono">
                            <span className={cn('font-semibold',
                              meta.sign === '+' ? 'text-green-600' :
                              meta.sign === '-' ? 'text-red-500' : 'text-[var(--text-tertiary)]')}>
                              {meta.sign !== ' ' ? meta.sign : ''}{e.days}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-[var(--text-secondary)]">{e.balance_before}</td>
                          <td className="px-4 py-2.5 font-mono font-semibold text-[var(--text-primary)]">{e.balance_after}</td>
                          <td className="px-4 py-2.5 text-[var(--text-secondary)]">{e.by || '—'}</td>
                          <td className="px-4 py-2.5 text-[var(--text-tertiary)] max-w-[200px] truncate">{e.note}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showAdjust && <AdjustmentModal employee={selectedEmp} onClose={() => setShowAdjust(false)} />}
      {showEncash && <EncashmentModal employee={selectedEmp} basicSalary={basicSalary} onClose={() => setShowEncash(false)} />}
    </div>
  );
}
